/**
 * @file product-catalog.tsx
 * @module app/modules/uProcure/vendors_management/vendor-profile/product-catalog
 * @description Vendor Profile > Product Catalog: every product assigned to the supplier, with
 *   last PO qty type, weight, Price Catalog cost and an editable MOQ (in the last PO's qty type);
 *   and the supplier's PO tonnage (tons). Both are used when draft POs are generated. Tonnage and
 *   MOQ each need their own permission (super admins always); the list exports to Excel. When the
 *   supplier has groups (Groups tab), each product's group is shown and, with "Edit supplier groups",
 *   set here (one row or the selected rows); draft POs are then made per group.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import APICallService from "../../../../../api/apiCallService";
import { PURCHASEORDER } from "../../../../../api/apiEndPoints";
import { POManagement } from "../../../../../utils/constants";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { error, success } from "../../../../../Global/toast";

const QTY: Record<number, string> = { 1: "Carton with Dozens", 2: "Dozen with Pieces", 3: "Piece", 4: "Carton with Pieces" };
const SHORT: Record<number, string> = { 1: "CWD", 2: "DWP", 3: "P", 4: "CWP" };

interface Row {
  variant: string;
  title: string;
  refKey?: string;
  brand?: string;
  sellingTypes: number[];
  weights: Record<number, number>;
  lastPo: { quantityType: number; quantity: number; refKey: string; sameSupplier: boolean } | null;
  costPrice: number | null;
  moq: { quantityType: number; quantity: number } | null;
  moqUnitType: number | null;
  /** the supplier group the product is in (null = no group) */
  group?: string | null;
}
interface GroupOption {
  _id: string;
  name: string;
  active: boolean;
}

const ProductCatalog = ({ businessId, supplierName }: { businessId: string; supplierName?: string }) => {
  const { selectedWarehouse } = useWarehouse();
  const [rows, setRows] = useState<Row[]>([]);
  const [tonnage, setTonnage] = useState<string>("");
  const [savedTonnage, setSavedTonnage] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [canEditTonnage, setCanEditTonnage] = useState(false);
  const [canEditMoq, setCanEditMoq] = useState(false);
  const canEdit = canEditTonnage || canEditMoq;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [reload, setReload] = useState(0);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [canEditGroups, setCanEditGroups] = useState(false);
  /** "all", "none" (no group) or a group id */
  const [groupFilter, setGroupFilter] = useState("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState("");
  const [moving, setMoving] = useState(false);
  const hasGroups = groups.length > 0;
  const groupName = (id?: string | null) => {
    const g = groups.find((x) => x._id === id);
    return g ? `${g.name}${g.active ? "" : " (switched off)"}` : "No group";
  };

  useEffect(() => {
    if (!businessId) return;
    (async () => {
      setLoading(true);
      const api = new APICallService(
        PURCHASEORDER.vendorCatalog,
        { warehouse: selectedWarehouse?.value ?? "" },
        { business: businessId },
        "",
        "",
        "",
        POManagement,
        selectedWarehouse?.value
      );
      const res = await api.callAPI();
      if (res && Array.isArray(res.records)) {
        setRows(res.records);
        setSavedTonnage(res.tonnageTon ?? null);
        setTonnage(res.tonnageTon != null ? String(res.tonnageTon) : "");
        // older servers send only canEdit
        setCanEditTonnage(Boolean(res.canEditTonnage ?? res.canEdit));
        setCanEditMoq(Boolean(res.canEditMoq ?? res.canEdit));
        setEdits({});
        setGroups(Array.isArray(res.groups) ? res.groups : []);
        setCanEditGroups(Boolean(res.canEditGroups));
        setSelected(new Set());
      }
      setLoading(false);
    })();
  }, [businessId, selectedWarehouse?.value, reload]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!q || r.title.toLowerCase().includes(q) || String(r.refKey ?? "").toLowerCase().includes(q)) &&
        (groupFilter === "all" || (groupFilter === "none" ? !r.group : r.group === groupFilter))
    );
  }, [rows, search, groupFilter]);

  /** Puts products into a group ("" = no group); saved at once. */
  const assign = async (variants: string[], group: string) => {
    setMoving(true);
    const api = new APICallService(
      PURCHASEORDER.updateSupplierGroup,
      { variants, group: group || null },
      { business: businessId, part: "products" },
      "",
      "",
      "",
      POManagement,
      selectedWarehouse?.value
    );
    const res = await api.callAPI();
    setMoving(false);
    if (res && typeof res.changed === "number") {
      setRows((list) => list.map((r) => (variants.includes(r.variant) ? { ...r, group: group || null } : r)));
      setSelected(new Set());
      setMoveTo("");
      // open draft POs follow at once (lines moved to the group's draft)
      const moved = Number(res.drafts?.moved) || 0;
      success(`${variants.length} product(s) ${group ? `in ${groupName(group)}` : "taken out of their group"}${moved ? `. ${moved} draft PO line(s) moved to the group's draft` : ""}`);
    }
  };

  const changedMoq = Object.entries(edits).filter(([v, val]) => {
    const r = rows.find((x) => x.variant === v);
    return String(r?.moq?.quantity ?? "") !== val.trim();
  });
  const tonnageChanged = (tonnage.trim() === "" ? null : Number(tonnage)) !== savedTonnage;

  /** Excel of the products shown (all, or those matching the search), with the supplier tonnage. */
  const exportExcel = () => {
    const header = ["Product", "SKU", "Brand", ...(hasGroups ? ["Group"] : []), "Sold As", "Last PO Qty", "Last PO Unit", "Last PO", "Last PO From Other Supplier", "Weight / Unit (kg)", "Cost Price (catalog)", "MOQ", "MOQ Unit"];
    const body = shown.map((r) => {
      const unit = r.moqUnitType;
      return [
        r.title,
        r.refKey ?? "",
        r.brand ?? "",
        ...(hasGroups ? [groupName(r.group)] : []),
        r.sellingTypes.map((t) => SHORT[t]).join(", "),
        r.lastPo ? r.lastPo.quantity : "",
        r.lastPo ? QTY[r.lastPo.quantityType] ?? "" : "",
        r.lastPo?.refKey ?? "",
        r.lastPo ? (r.lastPo.sameSupplier ? "No" : "Yes") : "",
        unit && r.weights[unit] ? r.weights[unit] : "",
        r.costPrice ?? "",
        r.moq?.quantity ?? "",
        unit ? QTY[unit] ?? "" : "",
      ];
    });
    const sheet = XLSX.utils.aoa_to_sheet([
      ["Supplier", supplierName ?? ""],
      ["PO tonnage (tons)", savedTonnage ?? "Not set"],
      ["Warehouse", selectedWarehouse?.label ?? ""],
      [],
      header,
      ...body,
    ]);
    sheet["!cols"] = header.map((h, i) => ({ wch: i === 0 ? 40 : Math.max(h.length + 2, 12) }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Product Catalog");
    const name = (supplierName ?? "supplier").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") || "supplier";
    XLSX.writeFile(book, `Product_Catalog_${name}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const save = async () => {
    const bad = changedMoq.find(([, val]) => val.trim() !== "" && !(Number.isInteger(Number(val)) && Number(val) >= 0));
    if (bad) {
      error("MOQ must be a whole number");
      return;
    }
    setSaving(true);
    const api = new APICallService(
      PURCHASEORDER.updateVendorCatalog,
      {
        ...(tonnageChanged && canEditTonnage ? { tonnageTon: tonnage.trim() === "" ? null : Number(tonnage) } : {}),
        moq: (canEditMoq ? changedMoq : []).map(([variant, val]) => {
          const r = rows.find((x) => x.variant === variant) as Row;
          return { variant, quantityType: r.moqUnitType ?? r.sellingTypes[0], quantity: val.trim() === "" ? null : Number(val) };
        }),
      },
      { business: businessId },
      "",
      "",
      "",
      POManagement,
      selectedWarehouse?.value
    );
    const res = await api.callAPI();
    setSaving(false);
    if (res && res.business) {
      success("Product catalog saved");
      setReload((n) => n + 1);
    }
  };

  return (
    <Card className="border">
      <Card.Body>
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-4 mb-5">
          <div>
            <label className="fs-14 fw-600 mb-2">PO tonnage (tons)</label>
            <Form.Control
              type="number"
              min={0}
              step="0.1"
              className="w-200px"
              value={tonnage}
              disabled={!canEditTonnage}
              title={canEditTonnage ? "" : "You are not allowed to change the PO tonnage"}
              placeholder="Not set"
              onChange={(e) => setTonnage(e.target.value)}
            />
            <div className="fs-12 text-gray-600 mt-1">Used to build this supplier&apos;s draft POs by weight</div>
          </div>
          <input
            className="form-control w-300px"
            placeholder="Search product or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="light-primary" onClick={exportExcel} disabled={loading || !shown.length}>
            Export Excel
          </Button>
          {canEdit && (
            <Button
              className="uw-btn uw-btn-primary"
              disabled={saving || (!changedMoq.length && !tonnageChanged)}
              onClick={save}
            >
              {saving ? <Spinner animation="border" size="sm" /> : `Save${changedMoq.length ? ` (${changedMoq.length} MOQ)` : ""}`}
            </Button>
          )}
        </div>
        {hasGroups && (
          <div className="d-flex flex-wrap align-items-center gap-3 mb-4" data-testid="pc-groups-bar">
            <Form.Select size="sm" className="w-200px" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="pc-group-filter">
              <option value="all">All groups</option>
              <option value="none">No group</option>
              {groups.map((g) => (
                <option key={g._id} value={g._id}>
                  {groupName(g._id)}
                </option>
              ))}
            </Form.Select>
            {canEditGroups && (
              <>
                <span className="fs-14 text-gray-700 ms-4">{selected.size} selected</span>
                <Form.Select size="sm" className="w-200px" value={moveTo} disabled={!selected.size || moving} onChange={(e) => setMoveTo(e.target.value)} data-testid="pc-move-to">
                  <option value="">Move selected to…</option>
                  {groups
                    .filter((g) => g.active)
                    .map((g) => (
                      <option key={g._id} value={g._id}>
                        {g.name}
                      </option>
                    ))}
                  <option value="none">No group</option>
                </Form.Select>
                <Button size="sm" variant="light-primary" disabled={!selected.size || !moveTo || moving} onClick={() => assign([...selected], moveTo === "none" ? "" : moveTo)} data-testid="pc-move">
                  {moving ? <Spinner animation="border" size="sm" /> : "Move"}
                </Button>
              </>
            )}
            <span className="fs-12 text-gray-600">Draft POs are made per group; a product is in one group.</span>
          </div>
        )}
        {loading ? (
          <div className="text-center py-10">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: "65vh" }}>
            <table className="table table-row-bordered align-middle gs-4 gy-2 mb-0 fs-14">
              <thead className="bg-light position-sticky top-0" style={{ zIndex: 1 }}>
                <tr className="fw-bold text-dark">
                  {hasGroups && canEditGroups && (
                    <th style={{ width: 36 }}>
                      <Form.Check
                        aria-label="Select all shown"
                        checked={shown.length > 0 && shown.every((r) => selected.has(r.variant))}
                        onChange={(e) => setSelected(e.target.checked ? new Set(shown.map((r) => r.variant)) : new Set())}
                        data-testid="pc-select-all"
                      />
                    </th>
                  )}
                  <th>Product</th>
                  {hasGroups && <th style={{ width: 200 }}>Group</th>}
                  <th>Sold as</th>
                  <th>Last PO</th>
                  <th className="text-end">Weight / unit</th>
                  <th className="text-end">Cost Price (catalog)</th>
                  <th style={{ width: 210 }}>MOQ (in last PO qty type)</th>
                </tr>
              </thead>
              <tbody>
                {!shown.length && (
                  <tr>
                    <td colSpan={6 + (hasGroups ? 1 : 0) + (hasGroups && canEditGroups ? 1 : 0)} className="text-center py-10 text-gray-600">
                      No products assigned to this supplier
                    </td>
                  </tr>
                )}
                {shown.map((r) => {
                  const unit = r.moqUnitType;
                  const value = edits[r.variant] ?? String(r.moq?.quantity ?? "");
                  return (
                    <tr key={r.variant}>
                      {hasGroups && canEditGroups && (
                        <td>
                          <Form.Check
                            aria-label={`Select ${r.title}`}
                            checked={selected.has(r.variant)}
                            onChange={(e) =>
                              setSelected((cur) => {
                                const next = new Set(cur);
                                if (e.target.checked) next.add(r.variant);
                                else next.delete(r.variant);
                                return next;
                              })
                            }
                          />
                        </td>
                      )}
                      <td style={{ minWidth: 240 }}>
                        <div className="fw-600">{r.title}</div>
                        <div className="fs-12 text-gray-600">
                          {r.refKey}
                          {r.brand ? ` · ${r.brand}` : ""}
                        </div>
                      </td>
                      {hasGroups && (
                        <td data-testid={`pc-group-${r.variant}`}>
                          {canEditGroups ? (
                            <Form.Select size="sm" value={r.group ?? ""} disabled={moving} onChange={(e) => assign([r.variant], e.target.value)} aria-label={`Group of ${r.title}`}>
                              <option value="">No group</option>
                              {groups
                                .filter((g) => g.active || g._id === r.group)
                                .map((g) => (
                                  <option key={g._id} value={g._id} disabled={!g.active}>
                                    {groupName(g._id)}
                                  </option>
                                ))}
                            </Form.Select>
                          ) : (
                            <span className={r.group ? "fw-600" : "text-gray-600"}>{groupName(r.group)}</span>
                          )}
                        </td>
                      )}
                      <td>{r.sellingTypes.map((t) => SHORT[t]).join(", ") || "-"}</td>
                      <td>
                        {r.lastPo ? (
                          <>
                            {r.lastPo.quantity} {SHORT[r.lastPo.quantityType]}
                            <div className="fs-12 text-gray-600">
                              {r.lastPo.refKey}
                              {!r.lastPo.sameSupplier && " (other supplier)"}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-600">No PO yet</span>
                        )}
                      </td>
                      <td className="text-end">{unit && r.weights[unit] ? `${r.weights[unit]} kg` : "-"}</td>
                      <td className="text-end">{r.costPrice ? r.costPrice.toLocaleString("en-US") : <span className="text-danger">Not set</span>}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            step={1}
                            value={value}
                            placeholder="No MOQ"
                            disabled={!canEditMoq || !unit}
                            title={canEditMoq ? "" : "You are not allowed to change MOQs"}
                            onChange={(e) => setEdits((p) => ({ ...p, [r.variant]: e.target.value }))}
                          />
                          <span className="fs-13 fw-600 text-nowrap" title={unit ? QTY[unit] : ""}>
                            {unit ? SHORT[unit] : ""}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProductCatalog;
